import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export interface ImportMapping {
  provider: string;
  shopifyCustomerId?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  tags?: string;
  acceptsEmailMarketing?: string;
  emailMarketingTrueValue?: string;
  emailMarketingUnsubscribedValue?: string;
  emailMarketingSuppressed?: string;
  acceptsSmsMarketing?: string;
  smsMarketingTrueValue?: string;
  smsMarketingUnsubscribedValue?: string;
  address?: {
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  birthMonth?: string;
  birthday?: string;
}

export enum ImportProvider {
  SHOPIFY = "shopify",
  KLAVIYO = "klaviyo",
  ATTENTIVE = "attentive",
  CUSTOM = "custom",
}

export const importProviderMapping: Record<ImportProvider, ImportMapping> = {
  [ImportProvider.SHOPIFY]: {
    provider: ImportProvider.SHOPIFY,
    shopifyCustomerId: "Customer ID",
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phone: "Phone",
    tags: "Tags",
    acceptsEmailMarketing: "Accepts Email Marketing",
    emailMarketingTrueValue: "yes",
    emailMarketingUnsubscribedValue: "no",
    acceptsSmsMarketing: "Accepts SMS Marketing",
    smsMarketingTrueValue: "yes",
    smsMarketingUnsubscribedValue: "no",
    address: {
      street1: "Default Address Address1",
      street2: "Default Address Address2",
      city: "Default Address City",
      state: "Default Address Province Code",
      zip: "Default Address Zip",
      country: "Default Address Country",
    },
  },
  [ImportProvider.KLAVIYO]: {
    provider: ImportProvider.KLAVIYO,
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phone: "Phone Number",
    acceptsEmailMarketing: "Email Marketing Consent",
    emailMarketingTrueValue: "SUBSCRIBED",
    emailMarketingUnsubscribedValue: "UNSUBSCRIBED",
    emailMarketingSuppressed: "Email Suppressions",
    acceptsSmsMarketing: "SMS Marketing Consent",
    smsMarketingTrueValue: "SUBSCRIBED",
    smsMarketingUnsubscribedValue: "UNSUBSCRIBED",
    address: {
      street1: "Address",
      street2: "Address 2",
      city: "City",
      state: "State / Region",
      zip: "Zip Code",
      country: "Country",
    },
  },
  [ImportProvider.ATTENTIVE]: {
    provider: ImportProvider.ATTENTIVE,
    email: "Email",
    phone: "Phone",
    acceptsSmsMarketing: "Eligible for Text Marketing",
    smsMarketingTrueValue: "TRUE",
  },
  [ImportProvider.CUSTOM]: {
    provider: ImportProvider.CUSTOM,
    email: "Email",
    firstName: "First Name",
    lastName: "Last Name",
    fullName: "Full Name",
    phone: "Phone",
    tags: "Tags",
    acceptsEmailMarketing: "Email Marketing Subscribed",
    emailMarketingTrueValue: "TRUE",
    emailMarketingUnsubscribedValue: "FALSE",
    acceptsSmsMarketing: "SMS Marketing Subscribed",
    smsMarketingTrueValue: "TRUE",
    smsMarketingUnsubscribedValue: "FALSE",
    address: {
      street1: "Address",
      street2: "Address 2",
      city: "City",
      state: "State",
      zip: "Zip Code",
      country: "Country",
    },
    birthMonth: "Birth Month",
    birthday: "Birthday",
  },
};

export enum WorkflowError {
  FAILED = "Workflow execution failed",
  CANCELED = "Customer import cancelled",
}

const CustomerImportProgressSchema_ = z.object({
  importId: z.string(),
  segmentName: z.string().optional(),
  error: createEnum(WorkflowError).optional(),
  provider: createEnum(ImportProvider).optional(),
  lastRow: z.number(),
  totalRows: z.number(),
  progress: z.number(),
  totalCustomersImported: z.number(),
  totalCustomersSkipped: z.number(),
  totalEmailSubscribers: z.number(),
  totalSmsSubscribers: z.number(),
  totalFailed: z.number(),
});

export const CustomerImportProgressSchema: z.ZodType<CustomerImportProgress> =
  CustomerImportProgressSchema_;
export interface CustomerImportProgress
  extends z.infer<typeof CustomerImportProgressSchema_> {}
